import { useQuery } from '@tanstack/react-query'
import React, { useState, useEffect } from 'react'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import LoadingPage from '../LoadingPage'
import _ from 'lodash'
import Box from '@mui/material/Box'
import SkipNavigation from '../../components/SkipNavigation'
import Loader from '../../components/Loader'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { type InstructorDistribution } from '../../models/Distribution'
import TermHeader from '../../components/TermHeader'
import { getUserProfile } from '../../store/slices/userProfileSlice/selectors'
import { DistributionManager, TermsManager, UserManager } from '../../APIManager'
import { AdminViewerCardListFeature } from '../../features/AdminViewerDashboardFeature/AdminViewerDashboardFeature'
import CustomTooltip from '../../components/CustomTooltip'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import GenericButton from '../../components/GenericButton'
import { ReactComponent as ExportIcon } from '../../assets/Export-icon.svg'
import { Typography } from '@mui/material'
import { grey } from '../../colors'

export const AdminViewerDashboard: React.FC = (): JSX.Element => {
  const [adminName, setAdminName] = useState('')
  const [surveys, setSurveys] = useState<InstructorDistribution[]>([])
  const [searchParams] = useSearchParams()
  const { id } = useParams()
  const termId = searchParams.get('termId')
  const user = getUserProfile()

  const { data: terms = [], isFetching: termsLoading } = useQuery({
    queryKey: ['terms'],
    staleTime: 1000,
    queryFn: async () => (await TermsManager.getAllTerms({ pageSize: 200 })).data,
    retry: false,
    enabled: true
  })

  const { data: instructorDistributions = [], isFetching: instructorDistributionsLoading } = useQuery({
    queryKey: ['getAdminDistributions', id, termId],
    queryFn: async () => {
      const params = { termId, userId: user.userId }
      if (user.userId) {
        return (await DistributionManager.getAdminDistributions(params)).data
      }
    },
    enabled: !!termId && !!user.userId && !id,
    retry: false
  })

  const { data: facultyData, isFetching: facultyLoading } = useQuery({
    queryKey: ['faculty', id, termId],
    queryFn: async () => {
      if (!id) return null
      const params = { termId }
      return (await UserManager.getFacultyAndStaffByIdApi(id, params)).data
    },
    enabled: !!id
  })

  useEffect(() => {
    if (facultyData) {
      setAdminName(`${facultyData.firstName} ${facultyData.lastName}`)
    }
  }, [facultyData])

  useEffect(() => {
    if (!id && user) {
      setAdminName(user?.name ?? 'Courses')
    }
  }, [user, id])

  const getSortedDistributions = (surveys: InstructorDistribution[]): InstructorDistribution[] => {
    const currentDate = new Date()

    const upcomingSurveys: InstructorDistribution[] = []
    const ongoingSurveys: InstructorDistribution[] = []
    const completedSurveys: InstructorDistribution[] = []

    surveys.forEach((survey) => {
      const start = new Date(survey.startTimestamp)
      const end = new Date(survey.endTimestamp)

      if (start > currentDate) {
        upcomingSurveys.push(survey)
      } else if (end < currentDate) {
        completedSurveys.push(survey)
      } else {
        ongoingSurveys.push(survey)
      }
    })

    const sortByCourse = ['courseName', 'sectionAbbr']
    const sortedOngoing = _.orderBy(ongoingSurveys, ['endTimestamp', ...sortByCourse], ['asc', 'asc', 'asc'])
    const sortedCompleted = _.orderBy(completedSurveys, ['releaseTimestamp', ...sortByCourse], ['asc', 'asc', 'asc'])
    const sortedUpcoming = _.orderBy(upcomingSurveys, ['startTimestamp', ...sortByCourse], ['asc', 'asc', 'asc'])

    return [...sortedOngoing, ...sortedCompleted, ...sortedUpcoming]
  }

  useEffect(() => {
    if (instructorDistributions?.length > 0) {
      const sortedInstructorDistributions = getSortedDistributions(instructorDistributions)
      setSurveys(sortedInstructorDistributions)
    } else {
      setSurveys([])
    }
  }, [instructorDistributions])

  if (instructorDistributionsLoading || facultyLoading || termsLoading) {
    return <Loader open={instructorDistributionsLoading} />
  }

  const contactUsTooltip = (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography fontSize="12px">
        Export functionality is currently unavailable. Please contact support for assistance.
      </Typography>
      <Link style={{ color: grey[0], textDecoration: 'underline' }} to="mailto:support@classranked.com">
        support@classranked.com
      </Link>
    </Box>
  )

  return (
    <Box>
      <SkipNavigation />
      <CustomHeader info={instructorDistributionsLoading ? PAGE_TITLES.ADMIN : adminName} />
      {instructorDistributionsLoading && <LoadingPage title={PAGE_TITLES.ADMIN} />}
      <AdminViewerCardListFeature
        list={surveys}
        isLoading={instructorDistributionsLoading}
        name={adminName}
        controls={
          <TermHeader showSeries={false} terms={terms} termsLoading={termsLoading}>

            <CustomTooltip title={contactUsTooltip}>
              <GenericButton disabled fontWeight='bold' buttonType={ButtonType.Secondary} endIcon={<ExportIcon style={{ opacity: '0.4' }} />} label='Export' onClick={() => { }} buttonSize={ButtonSize.Medium} />
            </CustomTooltip>
          </TermHeader>
        }
      />
    </Box>
  )
}
